import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import {
  listReorderPolicies,
  upsertReorderPolicy,
  bulkUpsertReorderPolicies,
  bulkUpsertReorderPoliciesFromCsv,
  deleteReorderPolicy,
  previewReorderPolicyImpact,
} from "@/lib/reorder-policies.functions";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Sprint 14 — Planning Policy Management UI.
 *
 * Operators can tune lead time + safety buffer (the inputs the Sprint 13
 * deterministic evaluator reads) without SQL. Engine unchanged.
 */
export const Route = createFileRoute("/_authenticated/app/admin/inventory-planning-policies")({
  component: PoliciesPage,
});

type Row = {
  id: string;
  facility_id: string;
  sku_id: string;
  lead_time_days: number;
  safety_buffer_days: number;
  reorder_point: number;
  reorder_qty: number;
  active: boolean;
  updated_at: string;
  skus?: { sku?: string; name?: string } | null;
};

function PoliciesPage() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState<Record<string, { lead?: number; buffer?: number }>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLead, setBulkLead] = useState<string>("");
  const [bulkBuffer, setBulkBuffer] = useState<string>("");

  const listFn = useServerFn(listReorderPolicies);
  const upsertFn = useServerFn(upsertReorderPolicy);
  const deleteFn = useServerFn(deleteReorderPolicy);
  const previewFn = useServerFn(previewReorderPolicyImpact);
  const bulkFn = useServerFn(bulkUpsertReorderPolicies);
  const csvFn = useServerFn(bulkUpsertReorderPoliciesFromCsv);

  const { data: rows = [], isLoading } = useQuery<Row[]>({
    queryKey: ["reorder-policies", tenantId, facilityId, search],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? undefined, search: search || undefined } }) as Promise<Row[]>,
  });

  const save = useMutation({
    mutationFn: async (row: Row) => {
      const edit = edits[row.id] ?? {};
      return upsertFn({
        data: {
          tenantId: tenantId!,
          row: {
            id: row.id,
            facilityId: row.facility_id,
            skuId: row.sku_id,
            leadTimeDays: edit.lead ?? row.lead_time_days,
            safetyBufferDays: edit.buffer ?? row.safety_buffer_days,
            active: row.active,
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Policy saved");
      qc.invalidateQueries({ queryKey: ["reorder-policies"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => deleteFn({ data: { tenantId: tenantId!, id } }),
    onSuccess: () => {
      toast.success("Policy deleted");
      qc.invalidateQueries({ queryKey: ["reorder-policies"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const previewImpact = async (row: Row) => {
    const edit = edits[row.id] ?? {};
    const lead = edit.lead ?? row.lead_time_days;
    const buffer = edit.buffer ?? row.safety_buffer_days;
    try {
      const r = await previewFn({
        data: { tenantId: tenantId!, facilityId: facilityId ?? undefined, leadTimeDays: lead, safetyBufferDays: buffer },
      });
      const delta = r.delta;
      toast(
        `Preview: ${r.proposed_reorder_count} SKUs would be flagged for reorder (Δ ${delta >= 0 ? "+" : ""}${delta} vs current)`,
      );
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const toggleSelect = (id: string) =>
    setSelected((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const applyBulk = async () => {
    const lead = bulkLead === "" ? undefined : Number(bulkLead);
    const buffer = bulkBuffer === "" ? undefined : Number(bulkBuffer);
    if (lead === undefined && buffer === undefined) {
      toast.error("Set at least one field");
      return;
    }
    const targets = rows.filter((r) => selected.has(r.id));
    try {
      await bulkFn({
        data: {
          tenantId: tenantId!,
          rows: targets.map((r) => ({
            id: r.id,
            facilityId: r.facility_id,
            skuId: r.sku_id,
            leadTimeDays: lead ?? r.lead_time_days,
            safetyBufferDays: buffer ?? r.safety_buffer_days,
            active: r.active,
          })),
        },
      });
      toast.success(`Updated ${targets.length} policies`);
      setBulkOpen(false);
      setSelected(new Set());
      setBulkLead(""); setBulkBuffer("");
      qc.invalidateQueries({ queryKey: ["reorder-policies"] });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Inventory Planning Policies</h2>
        <p className="text-sm text-muted-foreground">
          Tune the deterministic planning evaluator: <strong>days of supply ≤ lead time + safety buffer</strong> triggers reorder.
          Changes apply at the next 6-hour evaluation cycle.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs h-9"
        />
        <div className="flex-1" />
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={selected.size === 0}>
              Bulk edit {selected.size > 0 && `(${selected.size})`}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Bulk edit policies</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Leave a field blank to keep its current value. Applies to {selected.size} selected polic{selected.size === 1 ? "y" : "ies"}.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs space-y-1">
                  <span className="text-muted-foreground">Lead time (days)</span>
                  <Input type="number" min={0} max={365} value={bulkLead} onChange={(e) => setBulkLead(e.target.value)} className="h-8" />
                </label>
                <label className="text-xs space-y-1">
                  <span className="text-muted-foreground">Safety buffer (days)</span>
                  <Input type="number" min={0} max={90} value={bulkBuffer} onChange={(e) => setBulkBuffer(e.target.value)} className="h-8" />
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button onClick={applyBulk}>Apply</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <CsvImportDialog<{ sku: string; facilityId: string; leadTimeDays: number; safetyBufferDays: number; active?: boolean }>
          trigger={<Button size="sm" variant="outline">Import CSV</Button>}
          title="Import planning policies"
          templateHeaders={["sku", "facility_id", "lead_time_days", "safety_buffer_days", "active"]}
          sampleRow={["SKU-001", facilityId ?? "<facility-uuid>", "7", "3", "true"]}
          validateRow={(r, i) => {
            const sku = (r.sku ?? "").trim();
            const fac = (r.facility_id ?? "").trim();
            const lead = Number(r.lead_time_days);
            const buf = Number(r.safety_buffer_days);
            if (!sku) return { ok: false, error: `Row ${i + 2}: sku required` };
            if (!fac) return { ok: false, error: `Row ${i + 2}: facility_id required` };
            if (!Number.isFinite(lead) || lead < 0 || lead > 365) return { ok: false, error: `Row ${i + 2}: lead_time_days 0-365` };
            if (!Number.isFinite(buf) || buf < 0 || buf > 90) return { ok: false, error: `Row ${i + 2}: safety_buffer_days 0-90` };
            return {
              ok: true,
              value: { sku, facilityId: fac, leadTimeDays: lead, safetyBufferDays: buf, active: r.active !== "false" },
            };
          }}
          onImport={async (rowsIn) => {
            const res = await csvFn({ data: { tenantId: tenantId!, rows: rowsIn } });
            return {
              inserted: res.count,
              failed: res.errors.map((e, idx) => ({ row: idx + 2, error: `${e.sku}: ${e.error}` })),
            };
          }}
          onDone={() => qc.invalidateQueries({ queryKey: ["reorder-policies"] })}
        />
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[36px]">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={selected.size > 0 && selected.size === rows.length}
                  onChange={toggleAll}
                />
              </TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="w-[120px]">Lead time (d)</TableHead>
              <TableHead className="w-[140px]">Safety buffer (d)</TableHead>
              <TableHead className="w-[120px]">Reorder pt</TableHead>
              <TableHead className="w-[80px]">Active</TableHead>
              <TableHead className="w-[260px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No reorder policies yet.</TableCell></TableRow>
            ) : rows.map((r) => {
              const edit = edits[r.id] ?? {};
              const lead = edit.lead ?? r.lead_time_days;
              const buffer = edit.buffer ?? r.safety_buffer_days;
              const dirty = edit.lead !== undefined || edit.buffer !== undefined;
              return (
                <TableRow key={r.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      aria-label="Select policy"
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.skus?.sku ?? r.sku_id.slice(0, 8)}</div>
                    {r.skus?.name && <div className="text-xs text-muted-foreground truncate max-w-[280px]">{r.skus.name}</div>}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      value={lead}
                      onChange={(e) => setEdits((p) => ({ ...p, [r.id]: { ...edits[r.id], lead: Number(e.target.value) } }))}
                      className="h-8 w-20"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      max={90}
                      value={buffer}
                      onChange={(e) => setEdits((p) => ({ ...p, [r.id]: { ...edits[r.id], buffer: Number(e.target.value) } }))}
                      className="h-8 w-20"
                    />
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{r.reorder_point}</TableCell>
                  <TableCell>
                    <Badge variant={r.active ? "default" : "outline"} className="text-[10px]">
                      {r.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => previewImpact(r)}>Preview</Button>
                      <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(r)}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => del.mutate(r.id)}>Delete</Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        Bounds: lead time 0–365 days, safety buffer 0–90 days. Edits audit-logged.
      </p>
    </div>
  );
}